import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { importSpan } from 'features/playground/api/playgroundApi';
import {
  inputClass, modalClass, modalOverlayStyle, primaryButtonClass, drawerIconButtonClass,
} from 'features/playground/styles';

/**
 * Normalize a parsed turn to a role the model accepts: system→system,
 * assistant→assistant, everything else (user, tool/context, other) → user.
 */
function turnToMessage(turn) {
  const kind = turn.kind || turn.role;
  const role = kind === 'system' ? 'system' : (kind === 'assistant' ? 'assistant' : 'user');
  return { role, content: turn.content || '' };
}

/** Map a span's turns to messages; if nothing splits into roles cleanly, dump the
 *  whole content into one message rather than lose it. */
export function spanToMessages(parsed) {
  const turns = Array.isArray(parsed?.turns) ? parsed.turns : [];
  const messages = turns.map(turnToMessage).filter((m) => m.content && m.content.trim());
  if (messages.length) return messages;
  const blob = turns.map((t) => t.content).filter(Boolean).join('\n\n').trim();
  return blob ? [{ role: 'user', content: blob }] : [];
}

/**
 * SpanImportModal — paste a span ID and import it straight into the task (no
 * preview / no per-turn choosing). Its messages map directly onto the fields.
 */
export default function SpanImportModal({ onClose, onApply }) {
  const [spanId, setSpanId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !loading) onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose, loading]);

  const importNow = async () => {
    const id = spanId.trim();
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const p = await importSpan(id);
      const messages = spanToMessages(p);
      if (messages.length === 0) {
        setError('This span has no readable content to import.');
        setLoading(false);
        return;
      }
      onApply({
        messages,
        source_span_id: p.source_span_id || id,
        provider: p.provider || null,
        model: p.model || null,
        temperature: p.temperature ?? null,
      });
      onClose();
    } catch (e) {
      const code = e?.response?.data?.error;
      setError(
        code === 'span_not_found'
          ? 'No span with that ID in your team. Check the ID and that it belongs to this team.'
          : (e?.response?.data?.message || e.message || 'Failed to import span'),
      );
      setLoading(false);
    }
  };

  return createPortal(
    <div
      className="fixed inset-0 z-[300] flex items-center justify-center p-4"
      style={modalOverlayStyle}
      onMouseDown={() => !loading && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Import from span"
        className={`${modalClass} max-w-[460px] p-5 text-[color:var(--text-primary)]`}
        style={{ fontFamily: 'Inter, sans-serif' }}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <h2 className="text-[14px] font-semibold">Import from span</h2>
          <button type="button" onClick={onClose} aria-label="Close" title="Close"
            className={`${drawerIconButtonClass} -mr-1 -mt-1`}>
            <X size={15} />
          </button>
        </div>

        {error && <p role="alert" className="mt-3 text-[12px] text-[color:var(--feedback-error)]">{error}</p>}

        <label className="mt-4 block">
          <span className="text-[12px] text-[color:var(--text-muted)]">Span ID</span>
          <input
            autoFocus // eslint-disable-line jsx-a11y/no-autofocus
            value={spanId}
            onChange={(e) => setSpanId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') importNow(); }}
            placeholder="Paste an LLM span_id"
            className={`${inputClass} mt-1`}
          />
        </label>
        <p className="mt-2 text-[12px] text-[color:var(--text-muted)]">The span's messages fill this task. Edit freely after.</p>

        <div className="mt-5 flex justify-end">
          <button
            type="button"
            onClick={importNow}
            disabled={loading || !spanId.trim()}
            className={primaryButtonClass}
          >
            {loading ? 'Importing' : 'Import'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
